import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2, Sparkles, CheckCircle2 } from "lucide-react";
import { CHARACTERS, CharacterById } from "@/components/LanguageCharacters";

const STEPS = [
  {
    key: "age_range",
    title: "¿Qué edad tienes?",
    subtitle: "Esto nos ayuda a personalizar el tono.",
    options: [
      { value: "18-24", label: "18 a 24 años" },
      { value: "25-34", label: "25 a 34 años" },
      { value: "35-44", label: "35 a 44 años" },
      { value: "45+", label: "45 años o más" },
    ],
  },
  {
    key: "target_lang",
    title: "¿Qué idioma quieres practicar?",
    subtitle: "Puedes cambiarlo más adelante.",
    options: [
      { value: "en", label: "🇺🇸 Inglés" },
      { value: "fr", label: "🇫🇷 Francés" },
      { value: "es", label: "🇪🇸 Español" },
    ],
  },
  {
    key: "motivation",
    title: "¿Qué te motiva más?",
    subtitle: "Adaptaremos las prácticas a tu objetivo.",
    options: [
      { value: "atender_clientes", label: "Atender clientes extranjeros en la mueblería 🛋️" },
      { value: "cerrar_ventas", label: "Cerrar más ventas con clientes que no hablan español 💼" },
      { value: "viajar", label: "Viajar y desenvolverte en el extranjero ✈️" },
      { value: "curiosidad", label: "Curiosidad personal y crecimiento 🌱" },
    ],
  },
  {
    key: "self_level",
    title: "¿Qué tan cómodo te sientes hablando?",
    subtitle: "Sé honesto, no hay respuestas malas.",
    options: [
      { value: "principiante", label: "Principiante · empiezo desde cero" },
      { value: "intermedio", label: "Intermedio · me defiendo" },
      { value: "avanzado", label: "Avanzado · solo quiero pulir" },
    ],
  },
  {
    key: "minutes_per_day",
    title: "¿Cuántos minutos al día puedes practicar?",
    subtitle: "Pequeño y constante gana.",
    options: [
      { value: 5, label: "5 minutos · solo lo justo" },
      { value: 10, label: "10 minutos · cómodo" },
      { value: 15, label: "15 minutos · enfocado" },
      { value: 30, label: "30 minutos · maratón" },
    ],
  },
];

export default function LanguagesOnboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [character, setCharacter] = useState(() => localStorage.getItem("lang_guide") || "owl");
  const [submitting, setSubmitting] = useState(false);
  const [plan, setPlan] = useState(null);

  const totalSteps = STEPS.length + 1; // +1 for character selection
  const isCharacterStep = step === STEPS.length;

  const setAnswer = (key, value) => setAnswers((a) => ({ ...a, [key]: value }));

  const next = () => setStep((s) => Math.min(s + 1, totalSteps));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    setSubmitting(true);
    localStorage.setItem("lang_guide", character);
    try {
      const r = await api.post(`/languages/onboarding/submit`, {
        age_range: answers.age_range,
        target_lang: answers.target_lang,
        motivation: answers.motivation,
        self_level: answers.self_level,
        minutes_per_day: parseInt(answers.minutes_per_day, 10),
        character,
      });
      setPlan(r.data.plan);
      toast.success("¡Plan listo! Tu coach te recibe en el mapa.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No pudimos generar tu plan");
    } finally {
      setSubmitting(false);
    }
  };

  if (plan) {
    return <PlanReveal plan={plan} character={character} onContinue={() => navigate(`/idiomas/${answers.target_lang}`)} />;
  }

  const progressPct = Math.round((step / totalSteps) * 100);
  const currentStep = STEPS[step];
  const canContinue = isCharacterStep ? !!character : !!answers[currentStep?.key];

  return (
    <div className="max-w-2xl mx-auto" data-testid="onboarding-wizard">
      {/* Progress */}
      <div className="flex items-center gap-3 mb-8">
        <button
          onClick={() => (step === 0 ? navigate("/idiomas") : back())}
          className="text-[#6B7280] hover:text-[#1B2E7A]"
          data-testid="onboarding-back"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1 h-2 bg-[#F3F4F6] rounded-full overflow-hidden">
          <div
            className="h-full bg-[#1B2E7A] rounded-full transition-all duration-500"
            style={{ width: `${progressPct}%` }}
          />
        </div>
        <span className="text-xs text-[#6B7280] tabular-nums">{Math.min(step + 1, totalSteps)}/{totalSteps}</span>
      </div>

      {/* Hero */}
      <div className="flex justify-center mb-6">
        <CharacterById id={character} size={120} mood={isCharacterStep ? "cheer" : "idle"} />
      </div>

      {isCharacterStep ? (
        <CharacterStep character={character} setCharacter={setCharacter} />
      ) : (
        <Step
          step={currentStep}
          value={answers[currentStep.key]}
          setValue={(v) => setAnswer(currentStep.key, v)}
        />
      )}

      <div className="mt-8 flex items-center justify-between gap-3">
        <p className="text-xs text-[#6B7280]">
          {isCharacterStep
            ? "Último paso. Tu coach se prepara para ti."
            : "Tus respuestas crean un plan único en segundos."}
        </p>
        {isCharacterStep ? (
          <button
            onClick={submit}
            disabled={submitting || !character}
            className="px-6 py-3 rounded-xl bg-[#1B2E7A] text-white font-medium hover:bg-[#13205A] inline-flex items-center gap-2 disabled:opacity-40"
            data-testid="onboarding-submit"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Generando con IA...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4" /> Generar mi plan
              </>
            )}
          </button>
        ) : (
          <button
            onClick={next}
            disabled={!canContinue}
            className="px-6 py-3 rounded-xl bg-[#1B2E7A] text-white font-medium hover:bg-[#13205A] inline-flex items-center gap-2 disabled:opacity-40"
            data-testid="onboarding-next"
          >
            Siguiente <ArrowRight className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}

function Step({ step, value, setValue }) {
  return (
    <div className="text-center">
      <h1 className="font-heading text-3xl sm:text-4xl text-[#1A1A1A] leading-tight mb-2">
        {step.title}
      </h1>
      <p className="text-[#6B7280] mb-6">{step.subtitle}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-left">
        {step.options.map((opt) => {
          const selected = value === opt.value;
          return (
            <button
              key={opt.value}
              onClick={() => setValue(opt.value)}
              className={`px-4 py-4 rounded-2xl border-2 transition-all ${
                selected
                  ? "border-[#1B2E7A] bg-[#1B2E7A]/5 text-[#1B2E7A] font-medium"
                  : "border-[#E5E7EB] bg-white hover:border-[#D1D5DB]"
              }`}
              data-testid={`option-${step.key}-${opt.value}`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function CharacterStep({ character, setCharacter }) {
  return (
    <div className="text-center">
      <h1 className="font-heading text-3xl sm:text-4xl text-[#1A1A1A] leading-tight mb-2">
        Elige tu coach
      </h1>
      <p className="text-[#6B7280] mb-6">El personaje que te acompañará en tu viaje.</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {CHARACTERS.map((c) => {
          const Comp = c.component;
          const isActive = c.id === character;
          return (
            <button
              key={c.id}
              onClick={() => setCharacter(c.id)}
              className={`text-left rounded-2xl border-2 p-3 transition-all ${
                isActive ? "border-[#1B2E7A] bg-white shadow-md" : "border-[#E5E7EB] bg-white hover:border-[#D1D5DB]"
              }`}
              data-testid={`onb-character-${c.id}`}
            >
              <div className="flex justify-center">
                <Comp size={88} mood={isActive ? "cheer" : "idle"} />
              </div>
              <p className="font-heading text-sm text-[#1A1A1A] mt-2">{c.name}</p>
              <p className="text-[10px] text-[#6B7280] leading-tight">{c.tagline}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function PlanReveal({ plan, character, onContinue }) {
  return (
    <div className="max-w-xl mx-auto" data-testid="plan-reveal">
      <div className="text-center mb-6">
        <CharacterById id={character} size={140} mood="cheer" />
      </div>
      <div className="bg-white rounded-3xl border border-[#E5E7EB] p-6 md:p-8 shadow-sm">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#F4B81F] inline-flex items-center gap-1.5">
          <Sparkles className="h-3.5 w-3.5" /> Plan generado por IA
        </p>
        <h1 className="font-heading text-3xl text-[#1A1A1A] mt-1 mb-3">Tu camino está listo.</h1>
        {plan.summary_es && <p className="text-[#6B7280] mb-5">{plan.summary_es}</p>}

        {plan.coach_message_es && (
          <div className="bg-[#FEF3C7] border border-[#F4B81F]/40 rounded-2xl p-4 mb-5">
            <p className="text-sm text-[#854D0E] flex items-start gap-2">
              <Sparkles className="h-4 w-4 mt-0.5 shrink-0" /> {plan.coach_message_es}
            </p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 mb-5">
          <Stat label="Min/semana" value={plan.weekly_minutes_goal} />
          <Stat label="Lecciones/semana" value={plan.weekly_lessons_goal} />
        </div>

        {plan.milestones_es?.length > 0 && (
          <>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] mb-2">Tus primeras metas</p>
            <ul className="space-y-2 mb-6">
              {plan.milestones_es.map((m, i) => (
                <li key={i} className="text-sm text-[#1A1A1A] flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-[#1B2E7A] mt-0.5 shrink-0" /> {m}
                </li>
              ))}
            </ul>
          </>
        )}

        <button
          onClick={onContinue}
          className="w-full px-6 py-3 rounded-xl bg-[#1B2E7A] text-white font-medium hover:bg-[#13205A] inline-flex items-center justify-center gap-2"
          data-testid="plan-continue"
        >
          Empezar mi viaje <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-[#FFFFFF] rounded-xl p-4">
      <p className="text-[10px] uppercase tracking-[0.18em] text-[#6B7280]">{label}</p>
      <p className="font-heading text-2xl text-[#1A1A1A]">{value}</p>
    </div>
  );
}
